<template>
    <div class="min-h-screen flex bg-white dark:bg-gray-950">
        <!-- Branding panel, hidden below lg -->
        <div class="hidden lg:flex lg:w-1/2 relative flex-col justify-between overflow-hidden bg-gradient-to-br from-ink to-ink-3 p-12 text-on-ink">
            <div class="flex items-center gap-3">
                <img src="@/assets/img/logo.svg" alt="Logo gesti" class="h-9 w-auto"/>
                <span class="text-xl font-bold">Gesti</span>
            </div>

            <div class="max-w-md">
                <h1 class="text-3xl font-bold leading-tight text-balance">
                    Gérez vos biens locatifs sans y passer vos soirées.
                </h1>
                <p class="mt-4 text-on-ink-muted">
                    Loyers, quittances, locataires et SCI : tout centralisé au même endroit.
                </p>

                <div class="mt-10 space-y-4">
                    <div class="flex items-center gap-3">
                        <span class="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/10">
                            <font-awesome-icon :icon="['fa', 'home']" class="w-4 h-4"/>
                        </span>
                        <span class="text-sm font-medium">Suivi de tous vos biens</span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/10">
                            <font-awesome-icon :icon="['fa', 'users']" class="w-4 h-4"/>
                        </span>
                        <span class="text-sm font-medium">Locataires et baux centralisés</span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/10">
                            <font-awesome-icon :icon="['fa', 'euro-sign']" class="w-4 h-4"/>
                        </span>
                        <span class="text-sm font-medium">Loyers et quittances automatisés</span>
                    </div>
                </div>
            </div>

            <p class="text-xs text-on-ink-muted">&copy; {{ new Date().getFullYear() }} Gesti</p>

            <div class="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl"></div>
        </div>

        <!-- Form panel -->
        <div class="flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-20 xl:px-24">
            <div class="mx-auto w-full max-w-sm">
                <div class="mb-8 flex items-center gap-3 lg:hidden">
                    <img src="@/assets/img/logo.svg" alt="Logo gesti" class="h-9 w-auto"/>
                    <span class="text-xl font-bold text-gray-900 dark:text-gray-100">Gesti</span>
                </div>

                <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Content de vous revoir
                </h2>
                <p class="mt-1 mb-8 text-sm text-gray-500 dark:text-gray-400">
                    Connectez-vous pour accéder à votre espace.
                </p>

                <div class="space-y-5">
                    <div>
                        <label for="email-address" class="form-label">Email</label>
                        <input
                            id="email-address"
                            v-model="username"
                            name="email"
                            type="email"
                            autocomplete="email"
                            required
                            :class="formError ? 'form-control-error' : 'form-control'"
                            placeholder="vous@exemple.com"
                            @keyup.enter="login"
                        />
                    </div>
                    <div>
                        <label for="password" class="form-label">Mot de passe</label>
                        <input
                            id="password"
                            v-model="password"
                            name="password"
                            type="password"
                            autocomplete="current-password"
                            required
                            :class="formError ? 'form-control-error' : 'form-control'"
                            placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                            @keyup.enter="login"
                        />
                    </div>

                    <div class="flex justify-end">
                        <button
                            class="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                            @click='openPasswordForgiveModal()'
                        >
                            Mot de passe oublié ?
                        </button>
                    </div>

                    <LoaderButton title="Se connecter" @ok="login" defaultClass="w-full btn-primary" ref="btnValidate"></LoaderButton>
                </div>
            </div>
        </div>
    </div>
    <PrimaryModal
        title="Réinitialiser votre mot de passe"
        btnSuccessText="Envoyer"
        ref="forgetPasswordModal"
        @ok="sendResetEmail"
    >
        <template v-slot:content>
            <input
                v-model="passwordForgiveEmail"
                type="email"
                name="email"
                :class="passwordForgiveEmailError ? 'form-control-error' : 'form-control'"
            />
            <p
                v-if="passwordForgiveEmailError"
                class='text-red-500 text-xs mt-1'
            >Cette valeur n'est pas une adresse email valide.</p>
        </template>
    </PrimaryModal>
</template>

<script>
import PrimaryModal from './../../components/composant/PrimaryModal';
import LoaderButton from './../../components/form/LoaderButton';

export default {
    components: {
        PrimaryModal,
        LoaderButton
    },
    data () {
        return {
            username: '',
            password: '',
            passwordForgiveEmail: '',
            passwordForgiveEmailError: false,
            submitted: false,
            formError: false,
            loader: false,
        }
    },
    computed: {
        loggedIn() {
            return this.$store.state.auth.status.loggedIn;
        },
        checkCredentials() {
            return !this.username || !this.password
        }
    },
    created() {
        if (this.loggedIn) {
            this.$router.push({ name: 'dashboard'});
        }
    },
    methods: {
        async login() {
            if (this.checkCredentials) {
                return;
            }

            this.$clearToast();
            this.$refs.btnValidate.activateLoader();
            try {
                this.formError = false

                await this.$store.dispatch('auth/login', {
                    username: this.username,
                    password: this.password,
                }).catch((err) => {
                    this.$toast({title: 'Erreur', description: err}, this.$toastError);
                    this.formError = true
                });
            } catch (e) {
                console.log(e)
            }

            this.$refs.btnValidate.deactivateLoader();
        },
        openPasswordForgiveModal() {
            this.$refs.forgetPasswordModal.openModal();
            this.passwordForgiveEmail = this.username;
        },
        async sendResetEmail() {
            this.passwordForgiveEmailError = false;

            if (!this.$validator.validateEmail(this.passwordForgiveEmail)) {
                this.passwordForgiveEmailError = true;
                return;
            }

            await this.$api.auth.resetPasswordRequest(this.passwordForgiveEmail)
            .then(() => {
                this.$toast({
                    title: 'Succès',
                    description: "Un email de réinitialisation a été envoyé à l'adresse renseignée."
                }, this.$toastSuccess)

                this.$refs.forgetPasswordModal.dismissModal();
            })
            .catch(() => {
                this.$toast({
                    title: 'Erreur',
                    description: "Une erreur inatendu est survenu, veuillez réessayer plus tard. Contacter un administrateur si le problème persiste"
                }, this.$toastError)

                return;
            })
        }
    }
};
</script>
